import { Actions, Events } from "@Context/common/general/context";
import VRHeaderButtonType from "@Enums/VRHeaderButtonType";
import { SetHeaderAltSectionsEvent, VRHeaderButtonEvent, VRHeaderButtonProps } from "@Globals/types";
import { useGlobalContext } from "@hooks/index";
import { LayoutGroup } from "framer-motion";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import HeaderCenterSection from "./sections/header-center-section";
import HeaderLeftSection from "./sections/header-left-section";
import HeaderRightSection from "./sections/header-right-section";
import { HeaderBase } from "./styled-components";

const DEFAULT_HEIGHT = 92;

export interface IHeader {
  height?: number;
  showContent?: boolean;
}

const NavBarVR = ({ height = DEFAULT_HEIGHT, showContent = true }: IHeader) => {
  const { dispatch } = useGlobalContext();
  const hideForRoute = useLocation().state?.hideHeader === true;
  const [headerButtonType, setHeaderButtonType] = useState<VRHeaderButtonType | null>(VRHeaderButtonType.BACK);
  const [title, setTitle] = useState("");
  const [badge, setBadge] = useState<VRHeaderButtonProps["badge"]>();
  const [actions, setActions] = useState<VRHeaderButtonProps["actions"]>([]);
  const [leftSection, setLeftSection] = useState<React.ReactNode>(null);
  const [centerSection, setCenterSection] = useState<React.ReactNode>(null);
  const [rightSection, setRightSection] = useState<React.ReactNode>(null);

  const updateNavBarHeight = (height: number) => {
    dispatch({
      type: Actions.CoreActions.UpdateState,
      payload: {
        app: {
          navBarHeight: height,
        },
      },
    });
  };

  const onShowHeaderButton = ({ type, title, badge, actions }: VRHeaderButtonEvent) => {
    setTitle(title);
    setBadge(badge);
    setActions(actions || []);
    setHeaderButtonType(type);
  };

  const onHideHeaderButton = () => {
    setHeaderButtonType(null);
    setBadge(undefined);
    setActions([]);
  };

  const onSetAltSections = (event: SetHeaderAltSectionsEvent) => {
    setLeftSection(event?.leftSection || null);
    setCenterSection(event?.centerSection || null);
    setRightSection(event?.rightSection || null);
  };

  useEffect(() => {
    updateNavBarHeight(height);
    Events.app.showHeaderButton.uniq(onShowHeaderButton);
    Events.app.hideHeaderButton.uniq(onHideHeaderButton);
    Events.app.setHeaderAltSections.uniq(onSetAltSections);

    dispatch({
      type: Actions.ProgressionActions.GetProgressionConfig,
      payload: {
        cache_time: [60, "mins"],
        session_only: true,
        disableLoaders: true,
      },
    });

    return () => {
      updateNavBarHeight(0);
      Events.app.showHeaderButton.remove(onShowHeaderButton);
      Events.app.hideHeaderButton.remove(onHideHeaderButton);
      Events.app.setHeaderAltSections.remove(onSetAltSections);
    };
  }, []);

  return (
    <>
      <HeaderBase sx={{ height, visibility: showContent && !hideForRoute ? "visible" : "hidden" }}>
        <LayoutGroup>
          {leftSection || <HeaderLeftSection headerButtonType={headerButtonType} buttonTitle={title} badge={badge} actions={actions} />}
          {centerSection || <HeaderCenterSection showTabs={!headerButtonType} />}
          {rightSection || <HeaderRightSection actions={actions} />}
        </LayoutGroup>
      </HeaderBase>
    </>
  );
};

export default NavBarVR;
